/**
 * The shell every route renders inside: skip link, header, primary
 * navigation, and the one `<main>`.
 *
 * FRONTEND_DESIGN §3 fixes the primary navigation at four destinations —
 * Today, Textbook, Progress, Homework (three until #376 / D30, 2026-10-18) — and §8 fixes how they must behave. Two
 * requirements from §8 are structural here rather than incidental:
 *
 * - **Keyboard-complete with visible focus.** The skip link is the first
 *   focusable element on every page, so a keyboard user reaches content
 *   without walking the navigation. Focus styling is global (`global.css`)
 *   and never removed per-component.
 * - **Announced correctly.** `<nav aria-label>` names the landmark, and the
 *   current destination carries `aria-current="page"` — a state a screen
 *   reader announces — in addition to the underline. §7: never colour alone.
 *
 * The route title lives in `<h1>` inside each page rather than here, so every
 * route has exactly one `h1` naming where the founder is.
 */

import { NavLink, Outlet } from 'react-router';
import { ConsentGate } from '../features/consent/consent-gate.js';
import { useMessages, useProfileLocale } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import { AccountControl } from './account-control.js';
import { LanguageSwitch } from './language-switch.js';
import { Logo } from './logo.js';
import { TabBar, type Tab } from './tab-bar.js';
import { useIsPhone } from './use-is-phone.js';
import { CourseFocusProvider, useCourseFocus } from './course-focus.js';
import { CourseFocusPicker } from './course-focus-picker.js';
import { useMorphCommit } from './page-morph.js';

const messages = defineMessages({
  en: {
    skip: 'Skip to main content',
    primary: 'Primary',
    home: 'Home',
    progress: 'Progress',
    homework: 'Homework',
    prep: 'Prep',
  },
  he: {
    skip: 'דילוג לתוכן הראשי',
    primary: 'ניווט ראשי',
    home: 'בית',
    progress: 'התקדמות',
    homework: 'שיעורי בית',
    prep: 'הכנה למבחן',
  },
});

type LabelKey = 'home' | 'progress' | 'homework' | 'prep';

interface Destination {
  readonly to: string;
  readonly label: LabelKey;
}

/** §3. Experiment and Settings are secondary and are not in this list. */
const HOME: Destination = { to: '/', label: 'home' };

/**
 * The destinations for the focused course (#442), after the course-focus dropdown. Prep is not here: it does not
 * exist yet (#440). Today leaves the learner navigation but keeps its route.
 */
const COURSE_DESTINATIONS: readonly Destination[] = [
  // #376 (D30): homework feedback is the pilot's demonstration.
  { to: '/homework', label: 'homework' },
  { to: '/progress', label: 'progress' },
  // #440: exam prep, after Progress.
  { to: '/exam-prep', label: 'prep' },
];

/*
 * #581: there is no secondary navigation any more. Settings (My data, consent, courses, contact) is reached from the
 * account menu in the header (`account-control.tsx`), which renders for a launch-only session too. The menu's Settings goes
 * to `/settings` and on to `/settings/account`, which the consent gate replaces while feedback is not granted; the
 * required consent screen therefore links straight to `/settings/data` and `/settings/contact`, the two routes the gate
 * never replaces. That is where
 * §10.4's requirement now lives: the founder must be able to "stop early, export data, and find complete deletion"
 * without developer help, and Account -> Settings -> My data is the findable route. `/textbook` stays reachable by
 * URL only (#578).
 */

export function AppShell(): React.JSX.Element {
  return (
    <CourseFocusProvider>
      <Shell />
    </CourseFocusProvider>
  );
}

function Shell(): React.JSX.Element {
  const t = useMessages(messages);
  useProfileLocale(true);
  const { focused } = useCourseFocus();
  const phone = useIsPhone();
  useMorphCommit();
  const tabs: readonly Tab[] = [
    { to: HOME.to, label: t.home, icon: 'home' },
    { to: '/homework', label: t.homework, icon: 'homework' },
    { to: '/progress', label: t.progress, icon: 'progress' },
    { to: '/exam-prep', label: t.prep, icon: 'prep' },
  ];
  /* #648: `data-course` scopes the accent variables (tokens.css); none when no course is focused, so the neutral applies. */
  return (
    <div className="app-shell" data-course={focused?.colour}>
      <a className="skip-link" href="#main">
        {t.skip}
      </a>

      {phone ? (
        <header className="app-header app-header-phone">
          <Logo homeLabel={t.home} />
          <ul className="phone-focus">
            <CourseFocusPicker />
          </ul>
          <AccountControl withLanguage />
        </header>
      ) : (
        <header className="app-header">
          <Logo homeLabel={t.home} />
          <nav className="app-nav" aria-label={t.primary}>
            <ul>
              <li>
                <NavLink to={HOME.to} end>
                  {t[HOME.label]}
                </NavLink>
              </li>
              <CourseFocusPicker />
              {COURSE_DESTINATIONS.map((destination) => (
                <li key={destination.to}>
                  {/*
                   * No `end` here (#661): the marker means this section, so
                   * `/homework/:id` and `/homework/:id/feedback` keep Homework
                   * current, and the pill does not move while the learner goes
                   * deeper into it. `end` stays on Home alone, because `/` is a
                   * prefix of every path and would otherwise mark Home current
                   * everywhere. (This used to be `end` everywhere, so that
                   * `/textbook` did not stay current under
                   * `/textbook/import/:id`; Textbook is no longer a destination.)
                   *
                   * Do not add `viewTransition` here. React Router persists
                   * transition state to `sessionStorage` under
                   * `remix-router-transitions` when a navigation uses that
                   * flag, which would quietly turn on a browser-storage
                   * writer. §6.2's table is absolute that learning evidence
                   * and source text never reach browser storage, and a
                   * pathname carries source and attempt ids. The page morphs
                   * (#648) take another route: `installPageMorphs`
                   * (`page-morph.ts`, called from `main.tsx`) wraps
                   * `router.navigate` and starts the browser's own View
                   * Transitions API, which stores nothing. This shell only
                   * reports its commits through `useMorphCommit`.
                   *
                   * `aria-current="page"` is not written here: NavLink sets it
                   * itself when the link is active, and passing our own would
                   * override the one the router keeps in sync with the location.
                   * `routes.a11y.test.tsx` asserts it is present, so this
                   * depends on library behaviour that is checked rather than
                   * assumed.
                   */}
                  <NavLink to={destination.to}>
                    {t[destination.label]}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <LanguageSwitch />
          {/* One account button; renders for a launch-only session too - see account-control.tsx. */}
          <AccountControl />
        </header>
      )}
      {/*
       * `tabIndex={-1}` makes the skip link's target focusable without adding
       * it to the tab order, which is what moves the screen reader's reading
       * position rather than just scrolling the viewport.
       */}
      <main className="app-main" id="main" tabIndex={-1}>
        {/* #373: nothing behind this mounts until feedback consent is granted. */}
        <ConsentGate>
          <Outlet />
        </ConsentGate>
      </main>
      {phone ? <TabBar label={t.primary} tabs={tabs} /> : null}
    </div>
  );
}
